import { JsonPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Config } from '@environments/config';
import { Commands, Search } from '@local/client-contracts';
import { Developer } from '@local/common';
import { NativeServicesRpcService, ServicesRpcService } from '@shared/services';
import { ApplicationsService } from '@shared/services/applications.service';
import { isProdEnv } from '@shared/utils';
import { LogRecord } from '@unleash-tech/js-logger';
import { takeUntil } from 'rxjs/operators';
import { IActionItem } from 'src/app/bar/models/action-item.model';
import { GlobalErrorHandler } from 'src/app/global-error-handler';
import { InvokeCommand } from '../../models/invoke-command.model';
import { StackViewerComponent } from '../stack-viewer/stack-viewer.component';
import { ErrorItemDialog } from './error-item-dialog.component';

@Component({
  selector: 'error-item',
  templateUrl: './error-item.component.html',
  styleUrls: ['./error-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ErrorItemComponent implements OnInit {
  actions: IActionItem[];
  @Input() set selected(value: boolean) {
    this._selected = value;
    if (!this.selected && this.stackComp) this.stackComp.expanded = false;
  }
  @Input() model: Search.ErrorItem;
  @Input() index: number;
  @Output() invoke = new EventEmitter<InvokeCommand>();
  @ViewChild('stack') stackComp: StackViewerComponent;
  private actionsService: Developer.Actions;
  private _selected: boolean;
  set stackExpanded(value: boolean) {
    this.stackComp.expanded = value;
  }

  get selected() {
    return this._selected;
  }
  private jsonPipe: JsonPipe = new JsonPipe();

  constructor(
    public dialog: MatDialog,
    private applications: ApplicationsService,
    private services: ServicesRpcService,
    private errorHandler: GlobalErrorHandler,
    private nativeServices: NativeServicesRpcService
  ) {
    this.actionsService = (this.nativeServices || this.services).invokeWith(Developer.DeveloperActionsRpcInvoker, 'developeractions');
  }

  ngOnInit() {
    if (!isProdEnv()) {
      const e = new Error();
      e.message = this.model.message;
      e.stack = JSON.stringify(this.model.stack);
      this.errorHandler.error = e; // Notify error on the bar
    }
  }

  openDialog() {
    const dialogRef = this.dialog.open(ErrorItemDialog, {
      autoFocus: false,
      maxHeight: '300px',
      data: { resource: this.jsonPipe.transform(this.model.resource) },
    });

    dialogRef.componentInstance.copy
      .pipe(takeUntil(dialogRef.afterClosed()))
      .subscribe(() => this.onInvoke(<Commands.CopyClipboard>{ type: 'copy-clipboard', value: JSON.stringify(this.model.resource) }));
  }

  async onInvoke(command: Commands.Command): Promise<void> {
    this.invoke.emit({
      command,
      context: { resource: { id: this.model.id, kind: 'link-resource' }, linkId: this.model.linkId },
      invokerItem: this.model,
    });
  }

  async createIssue() {
    const appId = this.model.resource.appId;
    const additionalInfo = `
      ## App version
      | Module | Version |
      | --- | --- |
      | ${appId} | ${(await this.applications.one(appId)).version}`;

    let body: string = `
      ## Error:
           Message: ${this.model.message}

           Stack: ${this.model.stack
             .map((frame) => ` ${frame.functionName} @ ${frame.fileName} ${frame.line};${frame.column}`)
             .join('\n\t')}
      `;
    const record: LogRecord = {
      message: additionalInfo,
      type: 'error',
      params: {},
      scope: '',
      time: Date.now(),
    };
    const url = await this.actionsService.createIssueUrl(Config.version, record);
    window.open(url);
  }
}
